import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type { AccountInvitation, SharedAccountMember } from '../../lib/api-types';
import { billingKeys } from '../billing/billing.api';
import { userKeys } from '../users/users.api';

export const multiUserKeys = {
  all: ['account', 'members'] as const,
  members: ['account', 'members', 'list'] as const,
};

export async function createAccountInvitation(): Promise<AccountInvitation> {
  return (
    await apiRequest<{ invitation: AccountInvitation }>('/api/multiuser/invite', {
      method: 'POST',
    })
  ).invitation;
}

export async function joinSharedAccount(code: string): Promise<void> {
  await apiRequest('/api/multiuser/join', {
    body: jsonBody({ code }),
    method: 'POST',
  });
}

export async function fetchAccountMembers(): Promise<SharedAccountMember[]> {
  return (await apiRequest<{ members: SharedAccountMember[] }>('/api/multiuser/members')).members;
}

export async function updateAccountMember(
  memberId: string,
  action: 'approve' | 'reject',
): Promise<void> {
  await apiRequest(`/api/multiuser/members/${memberId}/approve`, {
    body: jsonBody({ action }),
    method: 'POST',
  });
}

export async function leaveSharedAccount(): Promise<void> {
  await apiRequest('/api/multiuser/leave', { method: 'POST' });
}

export function useAccountMembers() {
  return useQuery({ queryFn: fetchAccountMembers, queryKey: multiUserKeys.members });
}

function useAccountInvalidation() {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: multiUserKeys.all }),
      client.invalidateQueries({ queryKey: userKeys.current }),
      client.invalidateQueries({ queryKey: billingKeys.all }),
      client.invalidateQueries({ queryKey: ['finance'] }),
    ]);
  };
}

export function useCreateInvitation() {
  return useMutation({ mutationFn: createAccountInvitation });
}

export function useJoinSharedAccount() {
  const invalidate = useAccountInvalidation();
  return useMutation({ mutationFn: joinSharedAccount, onSuccess: invalidate });
}

export function useUpdateAccountMember() {
  const invalidate = useAccountInvalidation();
  return useMutation({
    mutationFn: ({ action, memberId }: { action: 'approve' | 'reject'; memberId: string }) =>
      updateAccountMember(memberId, action),
    onSuccess: invalidate,
  });
}

export function useLeaveSharedAccount() {
  const invalidate = useAccountInvalidation();
  return useMutation({ mutationFn: leaveSharedAccount, onSuccess: invalidate });
}
